import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	listApiKeys,
	type ApiKey,
	type CreatedApiKey,
} from "@/services/api-key.service";
import { ProfileApiKeysList } from "./ProfileApiKeysList";
import { CreateApiKeyDialog } from "./CreateApiKeyDialog";
import { RevealApiKeyDialog } from "./RevealApiKeyDialog";
import { ApiKeyUsageDialog } from "./ApiKeyUsageDialog";
import { ApiKeyUsageExamples } from "./ApiKeyUsageExamples";
import { ApiEndpointReference } from "./ApiEndpointReference";

// Shared body for both API key surfaces: Profile > API Keys and
// Settings > External App Integrations. The two differ only in their
// heading/chrome, so the state and dialogs live here once.
export function ApiKeysManager({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) {
	const [keys, setKeys] = useState<ApiKey[]>([]);
	const [loading, setLoading] = useState(true);
	const [createOpen, setCreateOpen] = useState(false);
	const [reveal, setReveal] = useState<string | null>(null);
	const [usageKey, setUsageKey] = useState<ApiKey | null>(null);

	function load() {
		setLoading(true);
		listApiKeys()
			.then(setKeys)
			.catch(() => toast.error("Failed to load API keys."))
			.finally(() => setLoading(false));
	}

	useEffect(() => {
		load();
	}, []);

	function handleCreated(created: CreatedApiKey) {
		setReveal(created.key);
		const { key: _omit, ...rest } = created;
		setKeys((curr) => [rest as ApiKey, ...curr]);
	}

	function handleRegenerated(id: string, keyPrefix: string) {
		setKeys((curr) =>
			curr.map((k) => (k.id === id ? { ...k, key_prefix: keyPrefix } : k)),
		);
	}

	function handleRevoked(id: string) {
		setKeys((curr) =>
			curr.map((k) =>
				k.id === id
					? { ...k, revoked_at: new Date().toISOString() }
					: k,
			),
		);
	}

	return (
		<>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h3 className="text-sm font-semibold text-foreground">
						{heading}
					</h3>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{description}
					</p>
				</div>
				<Button
					type="button"
					size="sm"
					onClick={() => setCreateOpen(true)}
					className="w-full sm:w-auto"
				>
					<Plus className="h-3.5 w-3.5" />
					Generate API key
				</Button>
			</div>

			<Separator className="my-4" />

			{loading ? (
				<div className="space-y-2">
					<Skeleton className="h-8 w-full" />
					<Skeleton className="h-8 w-full" />
					<Skeleton className="h-8 w-full" />
				</div>
			) : (
				<ProfileApiKeysList
					keys={keys}
					setReveal={setReveal}
					onRevoked={handleRevoked}
					onRegenerated={handleRegenerated}
					onViewUsage={setUsageKey}
				/>
			)}

			<div className="mt-6">
				<ApiEndpointReference />
			</div>

			<div className="mt-4">
				<ApiKeyUsageExamples />
			</div>

			<CreateApiKeyDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				onCreated={handleCreated}
			/>
			<RevealApiKeyDialog
				plaintext={reveal}
				onClose={() => setReveal(null)}
			/>
			<ApiKeyUsageDialog
				apiKey={usageKey}
				onClose={() => setUsageKey(null)}
			/>
		</>
	);
}
